import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { handleApiError } from "@/lib/errorHandler";

type TeamMemberRole =
  | "ACADEMIC_DIRECTOR"
  | "EXECUTIVE_DIRECTOR"
  | "INSTRUCTOR"
  | "OTHER";

interface TeamMember {
  id: string;
  name: string;
  role: string;
  title: string;
  photoUrl: string | null;
  bio: string;
  linkedInUrl: string | null;
  signatureUrl: string | null;
  position: number;
  isActive: boolean;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
}

interface FormData {
  name: string;
  role: TeamMemberRole;
  title: string;
  bio: string;
  photoUrl: string;
  linkedInUrl: string;
  signatureUrl: string;
  isActive: boolean;
}

export function TeamMemberModal({ isOpen, onClose, member }: ModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!member;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      role: "INSTRUCTOR",
      title: "",
      bio: "",
      photoUrl: "",
      linkedInUrl: "",
      signatureUrl: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (member) {
      reset({
        name: member.name,
        role: member.role as TeamMemberRole,
        title: member.title,
        bio: member.bio,
        photoUrl: member.photoUrl || "",
        linkedInUrl: member.linkedInUrl || "",
        signatureUrl: member.signatureUrl || "",
        isActive: member.isActive,
      });
    } else {
      reset({
        name: "",
        role: "INSTRUCTOR",
        title: "",
        bio: "",
        photoUrl: "",
        linkedInUrl: "",
        signatureUrl: "",
        isActive: true,
      });
    }
  }, [member, reset]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const createMutation = useMutation({
    mutationFn: (data: FormData) =>
      adminApi.createTeamMember({
        name: data.name,
        role: data.role,
        title: data.title,
        bio: data.bio,
        photoUrl: data.photoUrl || undefined,
        linkedInUrl: data.linkedInUrl || undefined,
        signatureUrl: data.signatureUrl || undefined,
        isActive: data.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      toast.success("Miembro creado exitosamente");
      handleClose();
    },
    onError: (error) => {
      handleApiError(error, "Error al crear el miembro");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) =>
      adminApi.updateTeamMember(id, {
        name: data.name,
        role: data.role,
        title: data.title,
        bio: data.bio,
        photoUrl: data.photoUrl || null,
        linkedInUrl: data.linkedInUrl || null,
        signatureUrl: data.signatureUrl || null,
        isActive: data.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      toast.success("Miembro actualizado exitosamente");
      handleClose();
    },
    onError: (error) => {
      handleApiError(error, "Error al actualizar el miembro");
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const onSubmit = (data: FormData) => {
    if (isEditing && member) {
      updateMutation.mutate({ id: member.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-lg shadow-xl animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Miembro" : "Nuevo Miembro"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium">
              Nombre completo *
            </label>
            <Input
              id="name"
              placeholder="Ej: Juan Pérez"
              {...register("name", { required: "El nombre es requerido" })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="role" className="block text-sm font-medium">
              Rol *
            </label>
            <select
              id="role"
              className="w-full px-3 py-2 border rounded-md"
              {...register("role", { required: "El rol es requerido" })}
            >
              <option value="ACADEMIC_DIRECTOR">Director Académico</option>
              <option value="EXECUTIVE_DIRECTOR">Director Ejecutivo</option>
              <option value="INSTRUCTOR">Instructor</option>
              <option value="OTHER">Otro</option>
            </select>
            {errors.role && (
              <p className="text-sm text-destructive">{errors.role.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="title" className="block text-sm font-medium">
              Título / Cargo *
            </label>
            <Input
              id="title"
              placeholder="Ej: Director de Tecnología"
              {...register("title", { required: "El título es requerido" })}
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="bio" className="block text-sm font-medium">
              Biografía *
            </label>
            <textarea
              id="bio"
              rows={4}
              className="w-full px-3 py-2 border rounded-md resize-none"
              placeholder="Breve descripción del miembro..."
              {...register("bio", { required: "La biografía es requerida" })}
            />
            {errors.bio && (
              <p className="text-sm text-destructive">{errors.bio.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="photoUrl" className="block text-sm font-medium">
              URL de Foto
            </label>
            <Input
              id="photoUrl"
              type="url"
              placeholder="https://..."
              {...register("photoUrl")}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="linkedInUrl" className="block text-sm font-medium">
              LinkedIn URL
            </label>
            <Input
              id="linkedInUrl"
              type="url"
              placeholder="https://linkedin.com/in/..."
              {...register("linkedInUrl")}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="signatureUrl" className="block text-sm font-medium">
              URL de Firma (para certificados)
            </label>
            <Input
              id="signatureUrl"
              type="url"
              placeholder="https://..."
              {...register("signatureUrl")}
            />
            <p className="text-xs text-muted-foreground">
              Imagen de la firma para usar en certificados.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              className="rounded"
              {...register("isActive")}
            />
            <label htmlFor="isActive" className="text-sm">
              Miembro activo (visible en la página pública)
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Guardando..." : isEditing ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
